import { JSX, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PaperAirplaneIcon, TrashIcon } from '@heroicons/react/24/outline';
import useAppStore from '../stores/useAppStore';
import { emailService } from '../services/api/emailService';
import { uiLogger } from '../utils/logger';
import toast from 'react-hot-toast';

export default function ComingSoonPage(): JSX.Element {
  const { user } = useAppStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Check if this is accessed from admin context
  const isAdminContext = location.pathname === '/admin/contact';

  const handleSendMessage = async (): Promise<void> => {
    if (!message.trim()) {
      toast.error('Please enter a message before sending.');
      return;
    }

    if (message.length > 1000) {
      toast.error('Message is too long. Please keep it under 1000 characters.');
      return;
    }

    setIsLoading(true);
    
    try {
      // Prepare email data
      const emailData = {
        to: import.meta.env.VITE_SEND_TO_EMAIL || 'info@fabrik28.ai',
        subject: isAdminContext ? 'Admin Contact Message' : 'New Message from Coming Soon Page',
        message: message.trim(),
        userEmail: user?.email || 'Unknown',
        userName: user?.name || 'Unknown User',
        timestamp: new Date().toISOString(),
        context: isAdminContext ? 'admin-contact' : 'coming-soon'
      };

      // Send email via email service
      const result = await emailService.sendMessage(emailData);

      if (result.success) {
        toast.success(result.message);
        setMessage(''); // Clear the message
        uiLogger.info('Message sent successfully', { 
          userEmail: user?.email, 
          context: isAdminContext ? 'admin-contact' : 'coming-soon' 
        });
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      uiLogger.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearMessage = (): void => {
    setMessage('');
    toast.success('Message cleared.');
  };

  const handleBackToAdmin = (): void => {
    // navigate('/admin');
  };

  const handleBackToHome = (): void => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header with context-aware navigation */}
     <header className="bg-white dark:bg-gray-800 shadow-sm">
          <div className="container-max section-padding">
            <div className="flex items-center justify-between py-3">
              <button
                onClick={handleBackToAdmin}
                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 transition-colors"
              >
                {/* ← Admin Dashboard */}
              </button>
              <button
                onClick={handleBackToHome}
                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 transition-colors"
              >
                Home
              </button>
            </div>
          </div>
        </header>


      {/* Main Content */}
      <main className="container-max section-padding py-8 sm:py-16 pb-32 sm:pb-24 md:pb-32">
        <div className="max-w-2xl mx-auto text-center">
          {/* Context-aware messaging */}
          <div className="mb-8 sm:mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full mb-6">
              {isAdminContext ? (
                <svg className="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              ) : (
                <svg className="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>
            
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              {isAdminContext ? 'Contact Our Team' : 'Launching in Preview Soon'}
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 mb-6 sm:mb-8">
              {isAdminContext 
                ? 'Have questions, feedback, or need support? We\'re here to help.' 
                : 'We will be in touch.'
              }
            </p>
          </div>

          {/* Message Form */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 sm:p-8 mb-8 sm:mb-8">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-700 dark:text-gray-100 mb-6">
              {isAdminContext 
                ? 'Send us a message and we\'ll get back to you soon.' 
                : 'You can send us a message.'
              }
            </h2>
            
            <div className="space-y-6">
              {/* Message Textarea */}
              <div>
                <label htmlFor="message" className="sr-only">
                  Your message
                </label>
                <textarea
                  id="message"
                  rows={6}
                  maxLength={1000}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100 resize-none"
                  placeholder={isAdminContext 
                    ? "Tell us about any issues, questions, or feedback you have about the platform..."
                    : "Tell us about your business challenges, automation needs, or any questions you have about Fabrik 28..."
                  }
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={isLoading}
                />
                <div className={`mt-2 text-sm ${message.length > 900 ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`}>
                  {message.length}/1000 characters
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-4">
                <button
                  onClick={handleSendMessage}
                  disabled={isLoading || !message.trim()}
                  className="inline-flex items-center justify-center px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 text-base w-full sm:w-auto"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <PaperAirplaneIcon className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </button>
                
                <button
                  onClick={handleClearMessage}
                  disabled={isLoading || !message.trim()}
                  className="inline-flex items-center justify-center px-6 py-3 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 text-base w-full sm:w-auto"
                >
                  <TrashIcon className="w-5 h-5 mr-2" />
                  Clear
                </button>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-16 sm:mb-8">
            <p>
              {isAdminContext 
                ? 'Our team typically responds within 24 hours during business days. For urgent issues, please include "URGENT" in your message.'
                : 'We\'re working hard to bring you the best AI automation experience. Your feedback helps us prioritize features that matter most to you.'
              }
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
